import React, { memo, useMemo } from 'react'
import { Text, Avatar, Flex } from '@chakra-ui/core'
import { Task } from 'constants/interfaces'
import { Post } from 'redux/reducers/feed'
import { CustomBadge } from 'components/helperStyles'
import useAvatar from 'hooks/useAvatar'
import { useUserName } from 'hooks/useUserName'
import colors from 'constants/colors'
import { css } from '@emotion/core'
import { getDropShadowFilter, getFloatingAnimation, getTaskBadge } from './utils'
import { StatusBadge } from './StatusBadge'

interface PersonalViewItemProps {
  userId: string
  tasks: Task[]
  posts: Post[]
  onClick?: () => void
  isFaded?: boolean
  colorIndex?: number
}

export const PersonalViewItem = memo(
  ({ userId, tasks, posts, onClick, isFaded = false, colorIndex = 0 }: PersonalViewItemProps) => {
    const avatar = useAvatar(userId)
    const userName = useUserName(userId)
    const totalCount = tasks.length + posts.length
    const hasActiveContent = !isFaded && totalCount > 0
    const shadowFilter = useMemo(() => getDropShadowFilter(colorIndex), [colorIndex])
    const floatingAnimation = useMemo(() => getFloatingAnimation(colorIndex), [colorIndex])
    const badge = useMemo(() => getTaskBadge(tasks), [tasks])

    return (
      <Flex
        onClick={onClick}
        flexDirection="column"
        alignItems="center"
        justifyContent="flex-start"
        cursor="pointer"
        css={css`
          opacity: ${isFaded ? 0.5 : 1};
          transition: opacity 200ms ease-in-out;

          &:hover {
            opacity: ${isFaded ? 0.7 : 1};
          }
        `}
      >
        <Flex
          mb="8px"
          css={css`
            transition: transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1);

            &:hover {
              transform: scale(1.1);
            }
            &:hover > div {
              animation-play-state: paused;
            }
          `}
        >
          <Flex
            position="relative"
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
            style={{ rowGap: 4 }}
            css={css`
              animation: ${floatingAnimation.pattern} ${floatingAnimation.duration}s ease-in-out
                ${floatingAnimation.delay}s infinite;
              will-change: transform;
            `}
          >
            <Avatar
              src={avatar}
              w="112px"
              h="112px"
              border="3px solid white"
              borderRadius="full"
              css={css`
                ${hasActiveContent ? `filter: ${shadowFilter};` : ''}
              `}
            />
            {totalCount > 0 && (
              <CustomBadge
                position="absolute"
                top="-5px"
                right="-5px"
                bg={colors.red2}
                border="2px solid white"
                minW="38px"
                height="38px"
                fontSize="14px"
                padding="0"
                style={{ borderRadius: '100%' }}
              >
                {totalCount}
              </CustomBadge>
            )}

            <StatusBadge type={badge.type} value={badge.value} />

            <Text fontSize="14px" fontWeight="bold" mt="7px" textAlign="center" maxWidth="100px" isTruncated>
              {userName || userId}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    )
  }
)
